import * as React from 'react';
import { useSearch } from '@elastic/react-search-ui';
import { parseDisplayOrder, sortByDisplayOrder } from '../../../../open-search/helper';

export function CheckboxFacet(props) {
  const {label, options, onSelect, onRemove, initialFacetValues = [], isFirstFacet = false, order, attribute} = props;
  const { filters, isLoading } = useSearch();

  // Auto-deselect for subsequent facets — mirrors Typesense CustomRefinementList logic
  React.useEffect(() => {
    if (isFirstFacet || isLoading) return;

    options.forEach(option => {
      if (option.selected && option.count === 0) {
        onRemove(option.value);
      }
    });

    // A value with no matching rows comes back as no bucket at all rather than a
    // bucket of zero, so the loop above cannot see it: the selection survived,
    // invisibly, and kept filtering. Compare the active filter against the
    // buckets that did arrive and drop whatever is missing. Without this,
    // narrowing to a model that lacks a checked GPU left the chart empty with
    // nothing on screen to untick — only Clear could recover it.
    const present = new Set(options.map(option => String(option.value)));
    const selected = filters?.find(filter => filter.field === attribute)?.values || [];
    selected.forEach(value => {
      if (!present.has(String(value))) onRemove(value);
    });
  }, [options, isFirstFacet, isLoading, filters, attribute]);

  const merged = React.useMemo(() => {
    const existing = new Map(options.map(o => [o.value, o]));
    const missing = initialFacetValues
      .filter(v => !existing.has(v))
      .map(v => ({value: v, count: 0, selected: false}));
    // Alphabetical by default, then apply the admin's manual value order (a
    // newline-separated list; unlisted values keep their alpha position after
    // the listed ones) — same mechanism as the open-search faceted search.
    const alphabetical = [...options, ...missing].sort((a, b) => String(a.value).localeCompare(String(b.value)));
    return sortByDisplayOrder(alphabetical, parseDisplayOrder(order));
  }, [options, initialFacetValues, order]);

  return (
    <fieldset>
      <legend>{label}</legend>
      {merged.map(option => {
        // A subsequent-facet value with no matches for the current selection is
        // unavailable — hide it entirely (d-none) so the list shows only what
        // the selection actually offers. The first facet always stays complete.
        const disabled = !isFirstFacet && option.count === 0;
        return (
          <label key={option.value} className={disabled ? 'd-none' : ''}>
            <input
              type="checkbox"
              checked={option.selected}
              data-checked={option.selected}
              disabled={disabled}
              onChange={() => {
                option.selected ? onRemove(option.value) : onSelect(option.value);
              }}
            />
            <span>{option.value} <span className='counter'>({option.count})</span></span>
          </label>
        );
      })}
    </fieldset>
  );
}
